import ButtonBase from '@ixigo/iui/ButtonBase';
import Popover from '@ixigo/iui/Popover';
import Typography from '@ixigo/iui/Typography';
import { AnimatedPulseCircle } from '@repo/shared/icons';
import cn from '@repo/shared/utils/cn';
import { useSearch } from '@tanstack/react-router';
import { Fragment, useEffect, useRef, useState } from 'react';
import type { BaseBookingDetail, CabUpdateResponse } from '@/api';
import { windowOpen } from '@/lib/pwa/base';
import TrackRide from '@/routes/_auth/-icons/track-ride';
import { showTrackRideTooltip } from './helper';
import styles from './index.module.css';
import { WaitingTimeChip } from './waiting-time-chip';

interface CabCommunicationProps {
	bookingDetail: Pick<BaseBookingDetail, 'commuteMsg' | 'bookingId'>;
	communication?: CabUpdateResponse | null;
	isTripActive: boolean;
	onStopPolling: () => void;
}

export function CabCommunication(props: CabCommunicationProps) {
	const { bookingDetail, communication, isTripActive, onStopPolling } = props;
	const { confirmation } = useSearch({ from: '/_auth/trips/$bookingId/' });
	const [showTrackFeature, setShowTrackFeature] = useState(false);
	const trackRideRef = useRef<HTMLDivElement>(null);
	const { commuteMsg, bookingId } = bookingDetail;
	const {
		stopPolling = false,
		msg = '',
		waitingInfo,
		trackingUrl,
		isTripEnded = false,
	} = communication || {};
	const communicationMsg = msg || commuteMsg;
	const msgStatus = waitingInfo?.status || 'info';

	// One-shot guard: backend's stopPolling flag is sticky once trip ends, and
	// onStopPolling typically triggers a state update (e.g. refreshing booking
	// data) that changes its own reference. Without this ref, the effect would
	// re-fire on every render and loop on /tripDetails forever.
	const hasStoppedPollingRef = useRef(false);
	useEffect(() => {
		if (stopPolling && !hasStoppedPollingRef.current) {
			hasStoppedPollingRef.current = true;
			onStopPolling();
		}
	}, [stopPolling, onStopPolling]);

	useEffect(() => {
		if (bookingId && trackingUrl && !confirmation) {
			const shouldShow = showTrackRideTooltip(bookingId);
			setTimeout(() => {
				setShowTrackFeature(shouldShow);
			}, 1500);
		}
	}, [bookingId, trackingUrl, confirmation]);

	if (!communication && !communicationMsg) {
		return null;
	}

	return (
		<ButtonBase
			disableRipple
			className={`bg-${msgStatus}-subtle flex items-center justify-between px-[15px] py-2 relative w-full`}
			{...(trackingUrl && { onClick: () => windowOpen(trackingUrl) })}
		>
			<div className="flex items-center justify-between gap-x-5">
				<Typography
					variant="body-xs"
					// biome-ignore lint/security/noDangerouslySetInnerHtml: <just a static msg to show current cab status>
					dangerouslySetInnerHTML={{ __html: communicationMsg }}
				/>
				{waitingInfo?.showTimer && (
					<WaitingTimeChip waitingInfo={waitingInfo} />
				)}
			</div>
			{trackingUrl && !isTripEnded ? (
				<Fragment>
					<div
						ref={trackRideRef}
						className="flex items-center justify-center rounded-full bg-blue-100 p-0.5"
					>
						<TrackRide className="text-link size-20px" />
					</div>
					<Popover
						placement="top-end"
						className={cn(
							'rounded-10 bg-inverse text-primary-inverse !-top-2 left-3',
							showTrackFeature && styles['animate-bounce-subtle'],
						)}
						show={showTrackFeature}
						arrow
						referenceRef={trackRideRef}
						onClose={() => setShowTrackFeature(false)}
						classes={{
							tip: '!-left-3',
						}}
					>
						<div className="p-15 text-xs">Tap to track your ride</div>
					</Popover>
				</Fragment>
			) : (
				isTripActive &&
				!stopPolling && (
					<div className="size-6 flex items-center justify-center">
						<AnimatedPulseCircle className="text-link" />
					</div>
				)
			)}
		</ButtonBase>
	);
}
